/**
 * Password Service
 * Handles password-related operations (forgot, reset, validation)
 */

import httpService from '../httpService.js'

class PasswordService {
  /**
   * Send forgot password email
   * @param {Object} data - Password reset data
   * @param {string} data.email - User email
   * @returns {Promise<Object>} Forgot password response
   */
  async forgotPassword(data) {
    const { email } = data

    const response = await httpService.post('/auth/forgot', {
      email: email.trim()
    })

    return response
  }

  /**
   * Reset password with token
   * @param {Object} data - Password reset data
   * @param {string} data.token - Reset token
   * @param {string} data.password - New password
   * @param {string} data.confirmPassword - Password confirmation
   * @returns {Promise<Object>} Reset password response
   */
  async resetPassword(data) {
    const { token, password, confirmPassword } = data

    if (password !== confirmPassword) {
      return {
        success: false,
        error: true,
        message: 'Passwords do not match'
      }
    }

    const response = await httpService.post('/auth/reset', {
      token,
      password
    })

    return response
  }

  /**
   * Change password for authenticated user
   * @param {Object} data - Password change data
   * @param {string} data.currentPassword - Current password
   * @param {string} data.newPassword - New password
   * @param {string} data.confirmPassword - Password confirmation
   * @returns {Promise<Object>} Change password response
   */
  async changePassword(data) {
    const { currentPassword, newPassword, confirmPassword } = data

    if (newPassword !== confirmPassword) {
      return {
        success: false,
        error: true,
        message: 'New passwords do not match'
      }
    }

    const response = await httpService.post('/auth/change-password', {
      currentPassword,
      newPassword
    })

    return response
  }

  /**
   * Validate password strength
   * @param {string} password - Password to validate
   * @returns {Object} Validation result with score and feedback
   */
  validatePasswordStrength(password) {
    const validations = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumbers: /\d/.test(password),
      hasSpecialChars: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    }

    const passedValidations = Object.values(validations).filter(Boolean).length
    const totalValidations = Object.keys(validations).length

    let strength = 'weak'
    let score = (passedValidations / totalValidations) * 100

    if (score >= 80) strength = 'strong'
    else if (score >= 60) strength = 'medium'
    else if (score >= 40) strength = 'fair'

    return {
      isValid: passedValidations >= 3, // At least 3 criteria must pass
      strength,
      score: Math.round(score),
      validations,
      feedback: this.getPasswordFeedback(validations)
    }
  }

  /**
   * Get password validation feedback
   * @param {Object} validations - Validation results
   * @returns {Array} Feedback messages
   */
  getPasswordFeedback(validations) {
    const feedback = []

    if (!validations.minLength) {
      feedback.push('Password must be at least 8 characters long')
    }
    if (!validations.hasUppercase) {
      feedback.push('Password must contain at least one uppercase letter')
    }
    if (!validations.hasLowercase) {
      feedback.push('Password must contain at least one lowercase letter')
    }
    if (!validations.hasNumbers) {
      feedback.push('Password must contain at least one number')
    }
    if (!validations.hasSpecialChars) {
      feedback.push('Password must contain at least one special character')
    }

    return feedback
  }
}

export default new PasswordService()
